import { For, Show, createSignal, useContext } from 'solid-js';
import clsx from 'clsx';
import { Button } from '../../../../ui-components/button/button';
import { Heading } from '../../../../ui-components/heading/heading';
import { EntitySlug, FormattedPostItem, pdfStore, setPdfStore } from '../../pdf-store';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { InfoIcon } from '../../../icons-library/info';
import { CrossIcon } from '../../../icons-library/cross';
import { DragHandleCviIcon } from '../../../icons-library/drag-handle-cvi';
import { StatusNote } from '../status-note/status-note';
import { EmptyState } from '../empty-state/empty-state';

/* DS Card surface (Storybook `components-card-card-action`) for one sortable row. */
const ITEM_SURFACE = 'relative flex flex-col gap-cvi-4 rounded-cvi-md border border-cvi-border-muted bg-cvi-bg-elevation-raised p-cvi-5 '
    + 'transition-shadow duration-200 ease-cvi-standard';

export const Arrangement = (props: {
    saveExistingCollection: () => void
    reset: () => void
}) => {

    const { localize } = useContext(AppContext);

    /* Which row is currently being dragged over — drives the highlight ring reactively
       instead of toggling a class on the DOM node. */
    const [ dragOverId, setDragOverId ] = createSignal<string | null>(null);

    const isMutating = () => pdfStore.status === 'creating' || pdfStore.status === 'updating-existing';

    const closestItemId = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        return target.closest<HTMLElement>('[data-id]')?.dataset.id ?? null;
    };

    const dragStart = (e: DragEvent) => {
        const id = closestItemId(e);
        if (!id) return;
        e.dataTransfer?.setData('text/plain', id);
    };

    const dragOver = (e: DragEvent) => {
        e.preventDefault();
        setDragOverId(closestItemId(e));
    };

    const dragLeave = (e: DragEvent) => {
        if (closestItemId(e) === dragOverId()) {
            setDragOverId(null);
        }
    };

    const drop = (e: DragEvent) => {
        e.preventDefault();
        setDragOverId(null);

        const draggedItemId = e.dataTransfer?.getData('text/plain');
        const droppedOntoItemId = closestItemId(e);

        if (!draggedItemId || !droppedOntoItemId || draggedItemId === droppedOntoItemId) return;

        const postsCopy = [ ...pdfStore.workingCollectionPosts ];

        const oldIndex = postsCopy.findIndex((item) => item.objectID === draggedItemId);
        const item = postsCopy[oldIndex];
        const newIndex = postsCopy.findIndex((item) => item.objectID === droppedOntoItemId);

        postsCopy.splice(newIndex, 0, item);

        // if moving up the list we need to remove the item from the old index
        if (newIndex < oldIndex) {
            postsCopy.splice(oldIndex + 1, 1);
        } else {
            postsCopy.splice(oldIndex, 1);
        }

        setPdfStore('workingCollectionPosts', postsCopy);
    };

    const removeItem = (id: string) => {
        const collection = [ ...pdfStore.workingCollectionPosts ];
        const index = collection.findIndex((item) => item.objectID === id);
        collection.splice(index, 1);
        setPdfStore('workingCollectionPosts', collection);
    };

    const ActionButtons = () => (
        <Show when={pdfStore.workingCollectionPosts.length}>
            <div class="flex flex-wrap justify-end gap-cvi-4 pt-cvi-6">
                <Button
                    label={localize('clear-all', 'Clear All')}
                    variant='ghost'
                    onClick={() => props.reset()}
                    disabled={isMutating()}
                    noBlockSpacing
                />

                <Show when={pdfStore.status === 'editing' && pdfStore.activeCollectionId}>
                    <Button
                        label={localize('pdf-builder-update-existing-button', 'Update')}
                        variant='secondary-neutral'
                        onClick={() => {
                            props.saveExistingCollection();
                        }}
                        disabled={isMutating()}
                        noBlockSpacing
                    />
                </Show>

                <Button
                    label={localize('pdf-builder-create', 'Create')}
                    onClick={() => setPdfStore('status', 'naming')}
                    variant='primary'
                    disabled={isMutating()}
                    noBlockSpacing
                />
            </div>
        </Show>
    );

    const CreatingStatus = () => (
        <Show when={pdfStore.status === 'creating'}>
            <StatusNote loading class="mb-cvi-5">
                {localize('pdf-creating-collection-status', 'Creating collection...')}
            </StatusNote>
        </Show>
    );

    const VerifyingPostsStatus = () => (
        <Show when={!pdfStore.workingCollectionPosts.length && pdfStore.status === 'editing'}>
            <StatusNote loading>
                {localize('pdf-verifying-status', 'Verifying posts...')}
            </StatusNote>
        </Show>
    );

    const PostNotFoundExplainer = () => (
        <StatusNote tone="highlight" icon={<InfoIcon size="medium" stroke="currentColor" ariaHidden={true} />}>
            {localize('pdf-post-unavailable', 'This post is not available and will be removed upon saving/creating the collection.')}
        </StatusNote>
    );

    const ArrangementItem = ({ post }: { post: FormattedPostItem; }) => (
        <li
            class={clsx(
                ITEM_SURFACE,
                isMutating()
                    ? 'cursor-default bg-cvi-bg-block-neutral text-cvi-fg-subtle'
                    : 'cursor-grab text-cvi-fg-default hover:shadow-elevation-default active:cursor-grabbing',
                !post.available && 'bg-cvi-bg-block-neutral',
            )}
            draggable={!isMutating()}
            data-id={post.objectID}
        >
            {/* Drop indicator: a brand bar in the gap above the target row, since a drop
                always inserts the dragged item *before* the row it lands on. */}
            <Show when={dragOverId() === post.objectID}>
                <span aria-hidden="true" class="pointer-events-none absolute inset-x-0 -top-cvi-3 h-cvi-2 rounded-cvi-sm bg-cvi-bg-interactive" />
            </Show>

            <div class="flex items-center gap-cvi-5">
                <span class="inline-flex shrink-0 text-cvi-fg-subtle" aria-hidden="true">
                    <DragHandleCviIcon size="medium" ariaHidden={true} />
                </span>

                <div class="min-w-0 flex-1">
                    <p class="m-0 label-sm font-cvi-medium uppercase text-cvi-fg-muted">
                        {pdfStore.entityNameMap[ post?.type as EntitySlug ]}
                    </p>
                    <p class="m-0 break-words body-md font-cvi-medium" innerHTML={post.postTitle} />
                </div>

                <Button
                    label={localize('remove', 'Remove')}
                    variant='ghost'
                    small
                    noBlockSpacing
                    iconStart={<CrossIcon size="small" ariaHidden={true} />}
                    onClick={() => removeItem(post.objectID)}
                    disabled={isMutating()}
                />
            </div>

            <Show when={!post.available}>
                <PostNotFoundExplainer />
            </Show>
        </li>
    );

    return (
        <div class="mt-cvi-10">
            <div class="mb-cvi-5 flex flex-wrap items-baseline justify-between gap-x-cvi-5 gap-y-cvi-2">
                <Heading tag="h2" variant='heading-lg' noBlockSpacing>
                    {localize('pdf-arrangement', 'Arrangement')}
                </Heading>

                <Show when={ pdfStore.workingCollectionPosts.length > 1 }>
                    <p class="m-0 body-sm font-cvi-regular text-cvi-fg-muted">
                        {localize('pdf-drag-and-drop-to-reorder', 'Drag and drop to reorder')}
                    </p>
                </Show>
            </div>

            <Show when={!pdfStore.workingCollectionPosts.length && pdfStore.status !== 'editing'}>
                <EmptyState>
                    <span innerHTML={localize('pdf-arrangement-explainer', 'Add posts to your PDF by searching for them and clicking on them')} />
                </EmptyState>
            </Show>

            <VerifyingPostsStatus/>

            <CreatingStatus/>

            <Show when={ pdfStore.workingCollectionPosts.length }>
                <ul
                    class="m-0 flex list-none flex-col gap-cvi-4 p-0"
                    onDragStart={ (e) => dragStart(e) }
                    onDragOver={ (e) => dragOver(e) }
                    onDragLeave={ (e) => dragLeave(e) }
                    onDrop={ (e) => drop(e) }
                >
                    <For each={ pdfStore.workingCollectionPosts }>{(post) => (
                        <ArrangementItem post={ post } />
                    )}</For>
                </ul>
            </Show>

            <ActionButtons/>
        </div>
    );
};
